import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const SCRIPT_ID = "google-gis-script";

let loaderPromise = null;
function loadGis() {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve(window.google);
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return loaderPromise;
}

/**
 * Renders Google's official "Sign in with Google" button. On success, POSTs
 * the ID token to /api/auth/google and calls onAuth(userObj) with the JSON
 * response so the parent page can update auth context and route.
 *
 * @param {{ onAuth: (user: any) => void, referralCode?: string, text?: string }} props
 */
export default function GoogleSignInButton({ onAuth, referralCode, text = "continue_with" }) {
  const btnRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      // Silent — env var missing. Parent falls back to email/password form.
      return;
    }
    let cancelled = false;
    loadGis()
      .then((google) => {
        if (cancelled || !btnRef.current) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async ({ credential }) => {
            try {
              const { data } = await api.post("/auth/google", {
                credential,
                referral_code: referralCode || null,
              });
              onAuth?.(data);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Google sign-in failed");
            }
          },
          // Popup avoids the "session_id in URL fragment" fragility of a redirect.
          ux_mode: "popup",
          auto_select: false,
        });
        google.accounts.id.renderButton(btnRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text,
          shape: "rectangular",
          logo_alignment: "left",
          width: 360,
        });
      })
      .catch(() => {
        // GIS script failed to load — leave the container empty so the
        // fallback branding row can show a disabled/hidden state.
      });
    return () => {
      cancelled = true;
    };
  }, [onAuth, referralCode, text]);

  if (!GOOGLE_CLIENT_ID) return null;
  return (
    <div className="w-full flex justify-center mb-4" data-testid="google-signin-container">
      <div ref={btnRef} />
    </div>
  );
}
